import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  nextDurableRowMutationIso,
  observeRowMutationHighWater,
  type RowMutationClockIo,
} from "./row-mutation-clock.mjs";

const ROW_MUTATION_CLOCK_PREFIX = "tb_public_web_row_mutation_clock_v1";

export const ROW_MUTATION_DOMAINS = {
  todos: "todo",
  savedArticles: "saved_article",
} as const;

export type RowMutationDomain =
  (typeof ROW_MUTATION_DOMAINS)[keyof typeof ROW_MUTATION_DOMAINS];

function clockBaseKey(domain: string, itemId: string): string {
  return `${ROW_MUTATION_CLOCK_PREFIX}:${domain}:${encodeURIComponent(itemId)}`;
}

const accountRowMutationClockIo: RowMutationClockIo = {
  read(domain, itemId) {
    return readAccountScopedStorage(clockBaseKey(domain, itemId));
  },
  write(domain, itemId, value) {
    try {
      writeAccountScopedStorage(clockBaseKey(domain, itemId), value);
    } catch {
      // If account storage is unavailable, the row/tombstone write will also
      // remain best effort. Never cross into another account as a fallback.
    }
  },
};

export function observeAccountRowMutationHighWater(
  domain: RowMutationDomain,
  itemId: string,
  ...versions: unknown[]
): number {
  return observeRowMutationHighWater(
    accountRowMutationClockIo,
    domain,
    itemId,
    ...versions,
  );
}

export function nextAccountRowMutationIso(
  domain: RowMutationDomain,
  itemId: string,
  observedUpdatedAt?: unknown,
): string {
  return nextDurableRowMutationIso(
    accountRowMutationClockIo,
    domain,
    itemId,
    observedUpdatedAt,
  );
}

type PulledActiveRow = {
  id?: unknown;
  clientId?: unknown;
  articleId?: unknown;
  link?: unknown;
  updatedAt?: unknown;
};

type PulledTombstone = {
  clientId?: unknown;
  articleId?: unknown;
  deletedAt?: unknown;
};

function normalizedId(value: unknown): string {
  return String(value || "").trim();
}

export function observePulledRowMutationClocks(input: {
  todos?: PulledActiveRow[] | null;
  deletedTodos?: PulledTombstone[] | null;
  savedArticles?: PulledActiveRow[] | null;
  deletedSavedArticles?: PulledTombstone[] | null;
}): void {
  for (const row of Array.isArray(input.todos) ? input.todos : []) {
    const itemId = normalizedId(row.clientId || row.id);
    if (itemId) {
      observeAccountRowMutationHighWater(
        ROW_MUTATION_DOMAINS.todos,
        itemId,
        row.updatedAt,
      );
    }
  }
  for (const row of Array.isArray(input.deletedTodos) ? input.deletedTodos : []) {
    const itemId = normalizedId(row.clientId);
    if (itemId) {
      observeAccountRowMutationHighWater(
        ROW_MUTATION_DOMAINS.todos,
        itemId,
        row.deletedAt,
      );
    }
  }
  for (const row of Array.isArray(input.savedArticles) ? input.savedArticles : []) {
    const itemId = normalizedId(row.articleId || row.link);
    if (itemId) {
      observeAccountRowMutationHighWater(
        ROW_MUTATION_DOMAINS.savedArticles,
        itemId,
        row.updatedAt,
      );
    }
  }
  for (const row of Array.isArray(input.deletedSavedArticles)
    ? input.deletedSavedArticles
    : []) {
    const itemId = normalizedId(row.articleId);
    if (itemId) {
      observeAccountRowMutationHighWater(
        ROW_MUTATION_DOMAINS.savedArticles,
        itemId,
        row.deletedAt,
      );
    }
  }
}
